import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  validateRecipients,
  previewEmail,
  sendEmailNow,
  sendTestEmail,
  listEmailSends,
  listSubscribers,
  listGroups,
  ALL_MEMBERS_GROUP_ID,
  ValidateRecipientsResponse,
  EmailQuota,
  EmailGroup,
  Subscriber,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import EmailNav from '../components/EmailNav';
import './EmailCompose.css';

type SendMode = 'list' | 'paste';

/**
 * Email Compose page — v1 ultra-lean.
 *
 * Flow:
 *   1. Admin pastes emails into a textarea
 *   2. Live validation (debounced) shows valid / invalid / suppressed counts
 *   3. Admin writes subject + body (rich-text-lite via a contenteditable
 *      div that captures HTML)
 *   4. Preview pane shows the branded rendered HTML
 *   5. Consent checkbox required before send
 *   6. Send → redirects to history page with success toast
 */
export default function EmailCompose() {
  const navigate = useNavigate();
  const { email: currentUserEmail, plan } = useAuth();
  const isPro = plan === 'pro';

  // Recipients mode toggle (Email v2 Phase 1):
  //   - 'list':  send to all active subscribers from the stored list
  //   - 'paste': v1 behaviour — admin pastes emails into the textarea
  // For Pro tier (Email v2 Phase 2), the 'list' tab shows a group
  // multi-select chip picker. Selecting only the auto "All members"
  // group is functionally equivalent to mode='list'; selecting any
  // subset sends mode='groups' with the specific groupIds.
  // Default determined after the subscribers count loads (see effect
  // below). Until then we start in 'paste' so the UI doesn't flicker.
  const [mode, setMode] = useState<SendMode>('paste');
  const [subscribersCount, setSubscribersCount] = useState<number | null>(null);

  // Phase 2 (Pro) — group multi-select state for the list mode.
  const [groups, setGroups] = useState<EmailGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([ALL_MEMBERS_GROUP_ID]);
  const [subscribersForCount, setSubscribersForCount] = useState<Subscriber[]>([]);

  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [sendMeCopy, setSendMeCopy] = useState(false);
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const [validation, setValidation] = useState<ValidateRecipientsResponse | null>(null);
  const [validating, setValidating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  const [quota, setQuota] = useState<EmailQuota | null>(null);

  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [error, setError] = useState('');
  // Assume on until the API says otherwise, so a slow load never flashes
  // the notice at a club that can send.
  const [sendingEnabled, setSendingEnabled] = useState(true);

  // Load quota on mount
  useEffect(() => {
    listEmailSends()
      .then(data => {
        setQuota(data.quota);
        setSendingEnabled(data.sendingEnabled !== false);
      })
      .catch(() => {
        // Endpoint returns 404 if feature not enabled — redirect
        navigate('/dashboard');
      });
    if (currentUserEmail) setReplyTo(currentUserEmail);
  }, [currentUserEmail, navigate]);

  // Load active-subscriber count + their group memberships once.
  // Default mode flips to 'list' if the club has any subscribers —
  // saves the admin a click for the common case. Pro tier also
  // fetches groups for the chip picker.
  useEffect(() => {
    listSubscribers({ status: 'active' })
      .then(res => {
        setSubscribersCount(res.count);
        setSubscribersForCount(res.subscribers);
        if (res.count > 0) setMode('list');
      })
      .catch(() => {
        setSubscribersCount(0);
      });

    if (isPro) {
      listGroups()
        .then(res => setGroups(res.groups))
        .catch(() => {
          // 403 if club downgraded mid-session, etc. — just hide the
          // multi-select UI.
        });
    }
  }, [isPro]);

  // Live unique recipient count from selected groups.
  // If 'all-members' is selected, the count is the full active list
  // (auto group includes everyone). Otherwise dedupe across selected
  // explicit groups by walking the subscribers' own groups arrays.
  const groupRecipientCount = (() => {
    if (!isPro || mode !== 'list') return null;
    if (selectedGroupIds.includes(ALL_MEMBERS_GROUP_ID)) {
      return subscribersForCount.length;
    }
    if (selectedGroupIds.length === 0) return 0;
    const selected = new Set(selectedGroupIds);
    let count = 0;
    for (const sub of subscribersForCount) {
      for (const g of sub.groups || []) {
        if (selected.has(g)) { count++; break; }
      }
    }
    return count;
  })();

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds(prev => {
      if (prev.includes(groupId)) {
        // Removing the last one isn't allowed — keep at least one selected
        if (prev.length === 1) return prev;
        return prev.filter(g => g !== groupId);
      }
      // Adding the auto group: clear explicit selections (since auto = all)
      if (groupId === ALL_MEMBERS_GROUP_ID) return [ALL_MEMBERS_GROUP_ID];
      // Adding any explicit group: drop the auto if present
      return [...prev.filter(g => g !== ALL_MEMBERS_GROUP_ID), groupId];
    });
  };

  // Debounced validation of pasted recipients
  const validationTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (validationTimeout.current) clearTimeout(validationTimeout.current);
    if (!recipients.trim()) {
      setValidation(null);
      return;
    }
    setValidating(true);
    validationTimeout.current = setTimeout(() => {
      validateRecipients(recipients)
        .then(setValidation)
        .catch(() => setValidation(null))
        .finally(() => setValidating(false));
    }, 500);
    return () => {
      if (validationTimeout.current) clearTimeout(validationTimeout.current);
    };
  }, [recipients]);

  // Debounced preview render
  const previewTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (previewTimeout.current) clearTimeout(previewTimeout.current);
    if (!bodyHtml.trim()) {
      setPreviewHtml('');
      return;
    }
    previewTimeout.current = setTimeout(() => {
      previewEmail(bodyHtml)
        .then(data => setPreviewHtml(data.html))
        .catch(() => setPreviewHtml(''));
    }, 700);
    return () => {
      if (previewTimeout.current) clearTimeout(previewTimeout.current);
    };
  }, [bodyHtml]);

  // Send-ready: subject + body + consent + at least one recipient.
  // Recipient gating differs by mode:
  //   - list mode: requires subscribersCount > 0
  //   - paste mode: requires validation.sendableCount > 0 (server-side
  //     filtered suppressed)
  const recipientsReady =
    mode === 'list'
      ? (isPro
          ? groupRecipientCount !== null && groupRecipientCount > 0
          : (subscribersCount !== null && subscribersCount > 0))
      : !!(validation && validation.sendableCount > 0);

  const canSend =
    sendingEnabled &&
    !sending &&
    recipientsReady &&
    subject.trim().length > 0 &&
    bodyHtml.trim().length > 0 &&
    consentConfirmed;

  // Test send — drops a single copy into the admin's own inbox without
  // touching quota or history. Lets you eyeball the real rendered email
  // (deliverability, inbox-vs-spam, image loading) before broadcasting.
  // Doesn't require recipients or consent — it's just to you.
  const canTestSend =
    sendingEnabled &&
    !testSending &&
    !sending &&
    subject.trim().length > 0 &&
    bodyHtml.trim().length > 0;

  const handleTestSend = async () => {
    setError('');
    setTestResult('');
    setTestSending(true);
    try {
      const res = await sendTestEmail({
        subject,
        bodyHtml,
        replyTo: replyTo || undefined,
      });
      setTestResult(`✓ Test sent to ${res.sentTo}. Check your inbox (and spam folder).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test');
    } finally {
      setTestSending(false);
    }
  };

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!consentConfirmed) {
      setError('You must confirm recipient consent before sending.');
      return;
    }
    setSending(true);
    try {
      // Pro tier on list mode → translate selectedGroupIds into the
      // server's mode='groups' contract. If only 'all-members' is
      // selected, mode='list' is equivalent and slightly cheaper
      // server-side (skips the group expansion).
      const sendMode: 'paste' | 'list' | 'groups' =
        mode === 'paste'
          ? 'paste'
          : (isPro
              && selectedGroupIds.length > 0
              && !(selectedGroupIds.length === 1 && selectedGroupIds[0] === ALL_MEMBERS_GROUP_ID))
            ? 'groups'
            : 'list';

      const res = await sendEmailNow({
        recipients: mode === 'list' ? '' : recipients,
        subject,
        bodyHtml,
        replyTo: replyTo || undefined,
        sendMeCopy,
        consentConfirmed,
        mode: sendMode,
        groupIds: sendMode === 'groups' ? selectedGroupIds : undefined,
      });
      navigate(`/email/history?just-sent=${res.sendId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="email-page-shell email-compose-page">
      <EmailNav />

      <div className="page-head">
        <div>
          <h1>Send Email</h1>
          <p className="subtitle">
            Send a branded email to a list of recipients.
            {quota && (
              <>
                {' '}
                {quota.monthlySendLimit === null
                  ? 'Unlimited sends this month (Pro plan).'
                  : `${quota.usedThisMonth} of ${quota.monthlySendLimit} sends used this month.`}
              </>
            )}
          </p>
        </div>
      </div>

      {!sendingEnabled && (
        <div className="compose-disabled-notice">
          <strong>GrassrootsFC only sends transactional emails.</strong>
          <p>
            Those are the ones that keep the club running, like registration
            confirmations and payment links. Newsletters and announcements go from
            your own email provider. We still manage your list here, groups and
            unsubscribes included. Export it as a CSV when you want to send.
          </p>
          <Link to="/email/subscribers" className="ebtn ebtn-primary ebtn-sm">Go to your list</Link>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSend} className="compose-layout">
        <div className="compose-form">
          {/* Recipients — mode toggle (Email v2 Phase 1) */}
          <div className="form-group">
            <div className="recipients-mode-tabs">
              <button
                type="button"
                className={mode === 'list' ? 'tab active' : 'tab'}
                onClick={() => setMode('list')}
                disabled={subscribersCount === 0}
                title={subscribersCount === 0 ? 'No subscribers yet — add some in the Subscribers tab' : undefined}
              >
                Send to my list
                {subscribersCount !== null && (
                  <span className="tab-count">{subscribersCount}</span>
                )}
              </button>
              <button
                type="button"
                className={mode === 'paste' ? 'tab active' : 'tab'}
                onClick={() => setMode('paste')}
              >
                Paste emails
              </button>
            </div>

            {mode === 'list' ? (
              <div className="recipients-list-mode">
                {subscribersCount === null && (
                  <p className="recipients-list-msg">Loading subscriber count…</p>
                )}
                {subscribersCount === 0 && (
                  <p className="recipients-list-msg recipients-list-empty">
                    Your subscriber list is empty. <a href="/email/subscribers">Add subscribers</a>{' '}
                    or switch to paste mode for a one-off send.
                  </p>
                )}

                {/* Club tier: simple "send to my list" view (Phase 1 UX) */}
                {!isPro && subscribersCount !== null && subscribersCount > 0 && (
                  <p className="recipients-list-msg recipients-list-count">
                    ✓ Will send to <strong>{subscribersCount}</strong> active{' '}
                    {subscribersCount === 1 ? 'subscriber' : 'subscribers'}.
                    Recipients already in your suppression list will be filtered server-side.
                  </p>
                )}

                {/* Pro tier: group multi-select chip picker (Phase 2 UX) */}
                {isPro && subscribersCount !== null && subscribersCount > 0 && (
                  <div className="group-picker">
                    <p className="group-picker-label">
                      Select audience:
                      {groups.length === 0 && (
                        <span className="group-picker-loading"> Loading groups…</span>
                      )}
                    </p>
                    <div className="group-chips">
                      {groups.map(g => {
                        const selected = selectedGroupIds.includes(g.groupId);
                        return (
                          <button
                            key={g.groupId}
                            type="button"
                            className={`group-chip ${selected ? 'selected' : ''} ${g.isAuto ? 'auto' : ''}`}
                            onClick={() => toggleGroup(g.groupId)}
                            title={g.isAuto ? 'Everyone on your list' : `Send to people tagged ${g.name}`}
                          >
                            <span className="group-chip-name">{g.name}</span>
                            <span className="group-chip-count">{g.memberCount}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="recipients-list-msg recipients-list-count">
                      ✓ Will send to{' '}
                      <strong>{groupRecipientCount}</strong>{' '}
                      unique{' '}
                      {groupRecipientCount === 1 ? 'recipient' : 'recipients'}.
                      Subscribers in multiple selected groups receive one email.
                      Suppressed addresses filtered server-side.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <label className="form-label">Recipients (one per line or comma-separated)</label>
                <textarea
                  className="form-textarea recipients-textarea"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="parent1@example.com&#10;parent2@example.com, parent3@example.com&#10;..."
                  rows={6}
                />
                <div className="recipients-status">
                  {validating && <span className="status-validating">Validating…</span>}
                  {!validating && validation && (
                    <>
                      <span className="status-pill status-ok">
                        ✓ {validation.sendableCount} will receive
                      </span>
                      {validation.invalidCount > 0 && (
                        <span className="status-pill status-warn">
                          {validation.invalidCount} invalid format
                        </span>
                      )}
                      {validation.suppressedCount > 0 && (
                        <span className="status-pill status-warn">
                          {validation.suppressedCount} in suppression list (skipped)
                        </span>
                      )}
                      {validation.error && (
                        <span className="status-pill status-err">{validation.error}</span>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Subject */}
          <div className="form-group">
            <label className="form-label">Subject</label>
            <input
              type="text"
              className="form-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Match this Sunday — important update"
              maxLength={200}
            />
          </div>

          {/* Body */}
          <div className="form-group">
            <label className="form-label">Body</label>
            <p className="form-hint">
              Plain text or simple HTML. Your club branding (badge, colours, footer) wraps automatically.
            </p>
            <textarea
              className="form-textarea body-textarea"
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder="Hi all,&#10;&#10;Quick update — training this Sunday is cancelled due to weather. Back to normal next week.&#10;&#10;Cheers,&#10;Coach"
              rows={10}
            />
          </div>

          {/* Reply-to */}
          <div className="form-group">
            <label className="form-label">Reply-to address</label>
            <input
              type="email"
              className="form-input"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="coach@yourclub.com"
            />
            <p className="form-hint">Replies from recipients go here. Defaults to your account email.</p>
          </div>

          {/* Options */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={sendMeCopy}
                onChange={(e) => setSendMeCopy(e.target.checked)}
              />
              <span>Send me a copy</span>
            </label>
          </div>

          {/* Consent */}
          <div className="form-group consent-block">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={consentConfirmed}
                onChange={(e) => setConsentConfirmed(e.target.checked)}
              />
              <span>
                I confirm these recipients have consented to receive emails from our club.
                (Required by GDPR — you are responsible for ensuring valid consent.)
              </span>
            </label>
          </div>

          {/* Test result banner */}
          {testResult && (
            <div className="just-sent-banner" style={{ marginTop: 8, marginBottom: 0 }}>
              <span className="just-sent-icon">📨</span>
              <div>{testResult}</div>
              <button
                type="button"
                className="just-sent-dismiss"
                onClick={() => setTestResult('')}
                aria-label="Dismiss"
              >×</button>
            </div>
          )}

          {/* Send buttons */}
          <div className="form-actions" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={!canTestSend}
              onClick={handleTestSend}
              title={currentUserEmail
                ? `Send a test copy to ${currentUserEmail} — doesn't use quota`
                : 'Test send'}
            >
              {testSending ? 'Sending test…' : '📨 Send test to me'}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSend}
            >
              {sending
                ? 'Sending…'
                : validation?.sendableCount
                  ? `Send to ${validation.sendableCount} recipients`
                  : 'Send'}
            </button>
          </div>
        </div>

        {/* Preview pane */}
        <div className="compose-preview">
          <div className="preview-header">
            <span className="preview-label">Preview</span>
            <div className="preview-mode-toggle">
              <button
                type="button"
                className={`mode-btn ${previewMode === 'desktop' ? 'active' : ''}`}
                onClick={() => setPreviewMode('desktop')}
              >Desktop</button>
              <button
                type="button"
                className={`mode-btn ${previewMode === 'mobile' ? 'active' : ''}`}
                onClick={() => setPreviewMode('mobile')}
              >Mobile</button>
            </div>
          </div>
          {previewHtml ? (
            <iframe
              className={`preview-iframe ${previewMode}`}
              srcDoc={previewHtml}
              title="Email preview"
            />
          ) : (
            <div className="preview-empty">
              Write a body to see the preview here.
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
